import React from 'react';
import { withStyles } from '@material-ui/core';
import styles from './Notepad.styles';
import FormView from 'components/FormView';
import NotepadForm from './Notepad.form';
import INotepadProperties, { INotepadHandle } from './Notepad.properties';
import { BACK_BUTTON } from 'components/common/ActionBar/constants';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import { Cancel as CancelIcon, CheckCircle, Edit } from '@markinson/uicomponents-v2/SvgIcons';
import { useSnackBar } from 'components/common/SnackBars/SnackBar.hooks';
import { Operation } from 'utils/operations';
import { useCreateNotepadDetail, useRetrieveNotepadDetail } from 'api/common/notepad/notepad';

const Notepad = (props: INotepadProperties, ref: React.Ref<INotepadHandle>) => {
    const {classes, operationMode, formValues, changeOperationMode, changeConfirmationDialog, resetForm, changeFieldValue, module} = props;
    const { setActionBar } = React.useContext(ActionBarContext);
    const { showSnackBar } = useSnackBar();
    const NotepadData = useRetrieveNotepadDetail(module);
    const createDiaryDetailMutation = useCreateNotepadDetail(module);

    React.useEffect(
        () => {
            setActionBarButtons();
        },
        [operationMode]
    );
    React.useEffect(
        () => {
            changeOperationMode(Operation.BROWSE);
        },
        []
    );

    const isFormDirty = React.useCallback(() => NotepadData?.data?.Notepad?.inlineObject?.Note !== formValues?.Note, [NotepadData?.data, formValues]);

    const setActionBarButtons = (): void => {
        setActionBar({
            leftIcons: [BACK_BUTTON],
            centerIcons: [],
            rightIcons: [
                {
                    label: 'Edit',
                    Icon: Edit,
                    disabled: operationMode === Operation.EDIT || !module?.selectedItem,
                    action: 'NotepadEdit',
                },
                {
                    label: 'Cancel',
                    Icon: CancelIcon,
                    disabled: operationMode === Operation.BROWSE,
                    action: 'NotepadCancel',
                },
                {
                    label: 'OK',
                    Icon: CheckCircle,
                    disabled: operationMode === Operation.BROWSE,
                    action: 'NotepadOk',
                },
            ]
        });
    };

    React.useImperativeHandle(
        ref,
        () => ({
            isChangesSavedOrDiscarded(): boolean {
                if (operationMode === Operation.BROWSE) return false;
                const result = isFormDirty();
                if (result) {
                    showSnackBar({ variant: 'warning', message: 'Please save or discard changes.' });
                }

                return result;
            },
            NotepadEdit(): void {
                changeOperationMode(Operation.EDIT);
            },
            NotepadCancel(): void {
                if (isFormDirty()) {
                    changeConfirmationDialog({
                        open: true,
                        title: 'Discard changes',
                        message: 'Are you sure you want to continue?',
                        okLabel: 'Discard',
                        onCancel: () => undefined,
                        onOk: async () => {
                            resetForm();
                            const {data} = await NotepadData.refetch();
                            changeFieldValue('Note', data?.Notepad?.inlineObject?.Note);
                            changeOperationMode(Operation.BROWSE);
                        }
                    });
                } else {
                    changeOperationMode(Operation.BROWSE);
                }
            },
            async NotepadOk(): Promise<void> {
                const result = await createDiaryDetailMutation.mutateAsync({[module?.entity]: module?.selectedItem, Note: formValues?.Note});
                if (result?.Status) {
                    changeOperationMode(Operation.BROWSE);
                }
            },
        }),
        [formValues, operationMode, NotepadData?.data?.Notepad?.inlineObject, module?.selectedItem]
    );

    return (
        <div className={classes.formContainer}>
            <FormView
                isLoading={false}
                formName={'NotepadForm'}
                schema={NotepadForm}
                includeTabsHeight={false}
                initialValues={NotepadData?.data?.Notepad?.inlineObject}
                valuesSchema={NotepadData?.data?.Notepad?.schema}
                operationMode={operationMode}
            />
        </div>
    );
};

export default withStyles(styles, { index: 1 })(React.memo(React.forwardRef(Notepad)));
